<template>
  <div class="site__centered application-steps">

    <Breadcrumbs
      v-if="isParentRole && programID && userId"
      :items="[
        { label: 'My Account', name: 'home', type: 'route' },
        { label: programName, id: programID, type: 'program' },
        { label: '', id: userId, type: 'child' },
        { label: 'My Application', name: '', type: 'route' }
      ]"
    />

    <b-tabs
      v-if="isTabIndexReady"
      v-model="tabIndex"
      vertical
      pills
      content-class="application-steps__tab-content"
      :nav-wrapper-class="isCollapsed ? 'application-steps__tabs is-tabs-collapsed' : 'application-steps__tabs'"
      nav-class="nav-left"
      active-nav-item-class="application-steps__tab_active"
    >
      <template #tabs-start>
        <!-- Header -->
        <li
          v-if="preRegFormsList.length"
          class="application-steps__header"
          @click="toggleCollapseTabs(false)"
        >
          Pre Registration Forms
          <span
            class="collapse-icon"
            :class="{'open': isCollapsed}"
          ></span>
        </li>
        <!-- Pre-reg tabs manually rendered -->
        <li
          v-for="(preRegForm, index) in preRegFormsList"
          :key="'pre-reg-form-' + index"
          class="application-steps__tab is-pre-reg"
          @click="setSpecificIndex(index)"
        >
          <a
            role="tab"
            :aria-posinset="index"
            class="nav-link application-steps__tab-link"
            :class="{
              'last-pre-reg': index === preRegFormsList.length - 1,
              'application-steps__tab_active': index === tabIndex,
            }"
            >
            <span class="font-weight-bold text-primary"><b>{{ preRegForm.name }}</b></span>
            <div
              v-if="preRegForm.status && !(['Approved', 'Not Started']).includes(preRegForm.status)"
              class="application-steps__tab-status"
            >{{ preRegForm.status }}</div>
            <div
              v-if="preRegForm.status && preRegForm.status === 'Approved'"
              class="application-steps__tab-status_approved"
            ></div>
          </a>
        </li>

        <!-- Header -->
        <li
          class="application-steps__header"
          :class="{ 'second-header': preRegFormsList.length }"
          @click="toggleCollapseTabs(true)"
        >
          Steps Application Form
          <span
            v-if="preRegFormsList.length === 0"
            class="collapse-icon"
            :class="{ 'open': isCollapsed }"
          ></span>
        </li>
      </template>

      <!-- Pre-Reg forms tabs -->
      <b-tab
        v-for="(preRegForm, index) in preRegFormsList"
        :key="'pre-reg-form-tab-' + index"
        title="Pre-reg form 1 test"
        title-item-class="d-none"
        title-link-class="d-none"
      >
        <pre-registration-form
          v-if="isDataFullPrepared"
          :form-index="index"
          @nextStep="goNextTabIfAvailable"
        />
      </b-tab>

      <!-- Family Form tabs -->
      <b-tab
        v-if="getIsUseFamilyForm"
        title-item-class="application-steps__tab"
        title-link-class="application-steps__tab-link"
        :disabled="hasUnfilledRequiredPreRegForms && !isApplicationAccepted"
        @click="setTabIndexQuery"
      >
        <template #title>
          <div>
            <span class="font-weight-bold text-primary"><b>Family Form</b></span>
          </div>
          <div
            v-if="familyFormStatusInfo.familyFormStatus && familyFormStatusInfo.familyFormStatus !== 'Approved'"
            class="application-steps__tab-status"
          >{{ familyFormStatusInfo.familyFormStatus  }}</div>
          <div
            v-if="isFilledFamilyForm"
            class="application-steps__tab-status_approved"
          ></div>
        </template>

        <FamilyForm
          @nextStep="goNextTabIfAvailable"
        />
      </b-tab>

      <b-tab
        v-for="(step, index) in filteredStepsList"
        :key="'step-form-tab-' + index"
        title-item-class="application-steps__tab"
        title-link-class="application-steps__tab-link"
        :disabled="step.isDisabled
        || (isFamilyTabActive && familyFormStatusInfo.isDisableToFillByFamilyForm)
        || (hasUnfilledRequiredPreRegForms && !isApplicationAccepted)"
        @click="setTabIndexQuery"
      >
        <template #title>
          <div>
            <span class="font-weight-bold text-primary">{{ stepIndex(index) }} <b>{{ step.name }}</b></span>
          </div>
          <div
            v-if="step.status && step.status !== 'Approved'"
            class="application-steps__tab-status"
          >{{ step.status }}</div>
          <div
            v-if="step.status && step.status === 'Approved'"
            class="application-steps__tab-status_approved"
          ></div>
        </template>

        <ApplicationStepForm
          v-if="step.preparedFields && isDataFullPrepared"
          :step="step"
          :form-fields="step.preparedFields"
          @nextStep="goNextTabIfAvailable"
          @applicationStale="handleApplicationStale"
        />
      </b-tab>

    </b-tabs>

    <group-users-list-modal />
    <PreRegistrationRequiredInfoModal />
  </div>
</template>

<script>
import { BTabs, BTab } from 'bootstrap-vue'

import { getUserData } from '@/auth/utils'

import FamilyForm from '@/views/Application/FamilyForm.vue'
import ApplicationStepForm from '@/views/Application/ApplicationStepForm.vue'
import store from '@/store'
import { mapGetters } from 'vuex'

import GroupUsersListModal from '@/components/site/group-users-list/GroupUsersListModal.vue'
import axios from '@axios'
import Breadcrumbs from '@/components/site/breadcrumb/Breadcrumbs.vue'
import { prepareProgramRegistrationInfo } from '@core/mixins/prepareProgramRegistrationInfo'
import PreRegistrationRequiredInfoModal from '@/views/PreRegistrationForms/PreRegistrationRequiredInfoModal.vue'
import PreRegistrationForm from '@/views/PreRegistrationForms/PreRegistrationForm.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    PreRegistrationForm,
    Breadcrumbs,
    FamilyForm,
    BTabs,
    BTab,

    ApplicationStepForm,
    GroupUsersListModal,
    PreRegistrationRequiredInfoModal,
  },
  filters: {
    toFixedNuber(value) {
      return value.toFixed(2)
    }
  },
  mixins: [ prepareProgramRegistrationInfo ],
  data() {
    return {
      userId: null,
      familyId: getUserData().family_id,
      type: ['parent', 'child'].includes(getUserData().role) ? 'Camper' : 'Staff',
      isParentRole: getUserData().role === 'parent',
      isCollapsed: false,
      isFamilyFormCollapsed: true,
      programID: null,
      isTabIndexReady: false,
      isLoadingData: false,
      // Guards the `tabIndex` watcher below from re-fetching application data
      // when the tab index is changed programmatically during the initial
      // page load (RESET_STATE resetting currentTabIndex, selectActiveTab()
      // auto-selecting a tab). loadApplication() already fetches fresh data
      // once, so those internal changes must not trigger a duplicate fetch.
      isInitialLoad: false,
    }
  },
  computed: {
    ...mapGetters({
      applicationId: 'applicationRegistration/getApplicationId',
      programName: 'applicationRegistration/getProgramName',
      familySettings: 'applicationRegistration/getFamilySettings',
      isApplicationAccepted: 'applicationRegistration/getIsApplicationAccepted',
      filteredStepsList: 'applicationRegistration/getFilteredStepsList',
      paymentContractIsExist: 'applicationRegistration/getPaymentContractIsExist',
      isExistCrossProgramsConditions: 'applicationRegistration/getIsExistCrossProgramsConditions',
      crossProgramConditionalFieldsData: 'applicationRegistration/getCrossProgramConditionalFieldsData',
      getIsUseFamilyForm: 'applicationRegistration/getIsUseFamilyForm',
      isFilledFamilyForm: 'applicationRegistration/getIsFilledFamilyForm',
      familyFormStatusInfo: 'applicationRegistration/getFamilyFormStatusInfo',
      familyFormStatus: 'applicationRegistration/getFamilyFormStatus',

      preRegFormsList: 'preRegistrationForm/getPreRegFormsList',
      hasUnfilledRequiredPreRegForms: 'preRegistrationForm/getHasUnfilledRequiredForms',
      hasUnfilledPreRegForms: 'preRegistrationForm/getHasUnfilledForms',
    }),
    tabIndex: {
      get: function () {
        return store.getters['applicationRegistration/getCurrentTabIndex']
      },
      set: function (val) {
        store.commit('applicationRegistration/SET_TAB_INDEX', val)
      }
    },
    isDataFullPrepared: {
      get: function () {
        return store.getters['applicationRegistration/getIsDataFullPrepared']
      },
      set: function (val) {
        store.commit('applicationRegistration/SET_IS_DATA_FULL_PRAPARED', val)
      }
    },
    isFamilyTabActive() {
      if (!this.getIsUseFamilyForm) {
        return false
      }
      if (!this.isFilledFamilyForm) {
        return true
      }
      if (!this.familySettings?.requirement_options?.includes('approval')) {
        return false
      }

      return !this.familyFormStatusInfo.isConfirmedFamilyForm
    },
    manageableUserId: {
      get() {
        return store.getters['preRegistrationForm/getManageableUserId']
      },
      set(val) {
        store.commit('preRegistrationForm/SET_MANAGEABLE_USER_ID', val)
      }
    },
  },
  watch: {
    async tabIndex() {
      // Skip re-fetching while the tab index is being set programmatically
      // during the initial page load (see isInitialLoad in data()). Only
      // genuine, user-driven tab changes (clicks) should trigger a refresh.
      if (this.isInitialLoad) {
        return
      }

      if (this.applicationId) {
        try {
          await this.setApplicationData(this.applicationId)
        } catch (error) {
          console.error('Error reloading application data on tab change:', error)
        }
      }
    },
    '$route.query': {
      async handler(newQuery, oldQuery) {
        // Prevent concurrent loads triggered by rapid navigation
        if (this.isLoadingData) {
          return
        }

        if ((oldQuery.app_id && newQuery.app_id !== oldQuery.app_id) || newQuery.user_id !== oldQuery.user_id) {
          this.userId = newQuery.user_id

          // beforeRouteEnter is not called when the component is already mounted
          // and only the query changes (e.g. child switched via Breadcrumbs).
          // Apply the same profile-review gate here, before resetting state.
          // this.familySettings is still valid at this point because the program
          // has not changed — only the child selection did.
          if (!newQuery.app_id && newQuery.user_id && store.state.authSettings.authSettings.require_child_profile_review) {
            const minReviewDate = store.state.authSettings.authSettings.min_child_profile_review_date
            const child = store.state.children.childrenList.find(c => c.id === newQuery.user_id)
            const reviewedAt = child?.extra_user_info?.profile_reviewed_at
            const needsReview = !reviewedAt || (minReviewDate && new Date(reviewedAt) < new Date(minReviewDate))

            if (needsReview) {
              this.$router.push({ name: 'application-select-child', params: { id: this.programID } })
              return
            }
          }

          this.isLoadingData = true
          try {
            await this.preLoadData()
          } catch (error) {
            console.error('Error loading application data on route change:', error)
          } finally {
            this.isLoadingData = false
          }
        }
      },
      deep: true
    }
  },
  async created() {
    const query = this.$router.currentRoute.query
    const role = getUserData().role

    // Safety net: a parent must always have an explicit child user_id or an
    // existing app_id. Without it we would silently create an application
    // linked to the parent themselves instead of a child.
    if (role === 'parent' && !query.user_id && !query.app_id) {
      const programId = this.$router.currentRoute.params.id
      this.$router.replace({ name: 'application-select-child', params: { id: programId } })
      return
    }

    if (role === 'staff' || role === 'child') {
      this.userId = getUserData().id
    } else {
      this.userId = query.user_id || null
    }

    await this.preLoadData()

    if (this.hasUnfilledPreRegForms) {
      this.$bvModal.show('pre-reg-required-info-modal')
    }
  },
  async beforeRouteEnter(to, from, next) {
    const role = getUserData().role
    const loggedUserId = getUserData().id
    const programId = to.params.id

    let program
    try {
      program = await store.dispatch('programs/fetchProgramById', programId)
    } catch (e) {
      next({ name: 'error-404' })
      return
    }

    if ((from.name !== 'archive-applications' && from.name !== 'home') && !program.is_active) {
      if (program.sharing_settings.type === 'only_selected_accounts') {
        const isAvaliableForThisUser = program.sharing_settings.selectedAccounts.some(item => item === loggedUserId)
        if (!isAvaliableForThisUser) {
          next({ name: 'error-404' })
          return
        }
      }
    }

    // For parents without an existing app_id and without a user_id, redirect
    // to child-selection BEFORE the component is created so created() never
    // runs with a missing user_id.
    if (role === 'parent' && !to.query.app_id && !to.query.user_id) {
      next({ name: 'application-select-child', params: { id: programId } })
      return
    }

    // For parents starting a NEW application (no app_id) with a child selected,
    // enforce the child profile review requirement. If the child's profile has
    // not been reviewed after the configured minimum date, send them back to
    // the child-selection page which will show the ReviewChildProfileModal.
    if (role === 'parent' && !to.query.app_id && to.query.user_id && store.state.authSettings.authSettings.require_child_profile_review) {
      const minReviewDate = store.state.authSettings.authSettings.min_child_profile_review_date

      try {
        // Re-use the children already loaded into the store by ApplicationSelectChild
        // or Breadcrumbs to avoid an extra HTTP request. Only fetch when the store
        // is genuinely empty (e.g. a cold direct-URL visit).
        if (!store.state.children.isLoaded) {
          await store.dispatch('children/fetchChildrenList')
        }

        const child = store.state.children.childrenList.find(c => c.id === to.query.user_id)
        const reviewedAt = child?.extra_user_info?.profile_reviewed_at
        const needsReview = !reviewedAt || (minReviewDate && new Date(reviewedAt) < new Date(minReviewDate))

        if (needsReview) {
          next({ name: 'application-select-child', params: { id: programId } })
          return
        }
      } catch (e) {
        console.error('Error checking child profile review status:', e)
        // Fail safe: send the parent through the review flow
        next({ name: 'application-select-child', params: { id: programId } })
        return
      }
    }

    next(async (vm) => {
      if (role === 'parent') {
        // Direct URL visit with a user_id but no app_id: look up an existing
        // application for that child so we don't create a duplicate.
        if (!from.name && !to.query.app_id && to.query.user_id) {
          try {
            const appResponse = await axios.get(`user-applications/${programId}/${to.query.user_id}`)
            const application = appResponse.data.data

            if (application.id) {
              vm.$router.replace({
                name: to.name,
                params: to.params,
                query: {
                  ...to.query,
                  app_id: application.id,
                },
              }).then(() => {
                window.location.reload()
              }).catch(error => {
                console.error('Error navigating to existing application:', error)
              })
            }
          } catch (error) {
            console.error('Error looking up existing application for child:', error)
            vm.$toast({
              component: ToastificationContent,
              props: {
                title: 'Unable to check for an existing application',
                text: 'Please refresh the page if this looks wrong.',
                icon: 'AlertTriangleIcon',
                variant: 'warning',
              },
            })
          }

          return
        }
      }
    })
  },
  methods: {
    async preLoadData() {
      this.isDataFullPrepared = false
      this.isInitialLoad = true

      this.programID = this.$router.currentRoute.params.id

      try {
        await this.loadPreRegFormsList()
        await this.loadApplication()
        await this.selectActiveTab()
      } catch (error) {
        console.error('Error during preLoadData:', error)
      } finally {
        this.isDataFullPrepared = true
        // selectActiveTab() sets tabIndex via $nextTick; wait for it to flush
        // before releasing the guard so the tabIndex watcher doesn't fire a
        // redundant data refetch for that programmatic change.
        this.$nextTick(() => {
          this.isInitialLoad = false
        })
      }
    },
    async loadPreRegFormsList() {
      this.manageableUserId = this.userId

      try {
        await store.dispatch('preRegistrationForm/fetchUserAttributes', { user_id: this.manageableUserId })
        await store.dispatch('preRegistrationForm/fetchPreRegFormsList', {
          userId: this.manageableUserId,
          program_id: this.programID,
        })

        await store.commit('preRegistrationForm/SET_FORMS_DATA')
        await store.commit('preRegistrationForm/SET_FORMS_VALUES_FROM_USER_ATTRIBUTES')
        await store.commit('preRegistrationForm/SET_FORMS_VALUES_FROM_FIELDS_RESULTS')
      } catch (error) {
        console.error('Error loading pre-registration forms list:', error)
        // Re-throw so preLoadData can decide how to handle it
        throw error
      }
    },
    async loadApplication() {
      store.commit('applicationRegistration/RESET_STATE')
      store.commit('applicationRegistration/SET_TYPE', this.type)
      store.commit('applicationRegistration/SET_USER_ID', this.userId)

      store.commit('SET_LOADING_STATUS', true)

      try {
        const applicationId = this.$router.currentRoute.query.app_id
        if (applicationId) {
          store.commit('applicationRegistration/SET_APPLICATION_ID', applicationId)
          await this.setApplicationData()
        }

        if (this.userId) {
          if (this.isParentRole) {
            // `/my-children/{id}` is a parent-only endpoint (it looks up one of the
            // authenticated parent's own children). For staff/child(student) roles,
            // userId is the logged-in user's own id, not a "child" of theirs, so we
            // must not call that endpoint for them - reuse their already-loaded
            // logged-in user data instead.
            await store.dispatch('applicationRegistration/fetchChildData', this.userId)
          } else {
            store.commit('applicationRegistration/SET_USER_DATA', getUserData())
          }
        }

        if (this.programID) {
          await store.dispatch('applicationRegistration/fetchUserAttributes', {
            user_id: this.userId,
          })

          store.commit('applicationRegistration/SET_PROGRAM_ID', this.programID)

          if (this.familyId) {
            await store.dispatch('applicationRegistration/fetchFamilyData', {
              family_id: this.familyId,
            })
          }

          const program = await store.dispatch('programs/fetchProgramById', this.programID)
          store.commit('applicationRegistration/SET_PROGRAM', program)
          await store.dispatch('applicationRegistration/prepareFamilyForm', {
            family_settings: program.family_settings,
            family_form: program.family_form,
          })
          store.commit('applicationRegistration/SET_STEP_STATUS')

          await this.prepareData()
        }

        if (!this.isFilledFamilyForm) {
          this.isFamilyFormCollapsed = false
        }

        if (applicationId) {
          await store.dispatch('alertMessage/fetchInfoMessages',{ application_id: applicationId })
        }
      } catch (error) {
        console.error('Error loading application:', error)
      } finally {
        store.commit('SET_LOADING_STATUS', false)
      }
    },
    async prepareData() {
      await store.dispatch('applicationRegistration/fetchPaymentFees', this.programID)

      await this.prepareCrossProgramsConditions()
      await this.prepareConditionsFromSteps()
      await this.setFormsData()
      await this.checkIfAllFieldsInStepAreHidden()
      await this.enableSteps()
      await this.disableEditingStepsIfpaymentContractIsExist()
      await this.disableEditingStepsByEditingApplicationPermissions()
      await this.checkHiddenStepsBeforeApplicationAcceptance()
    },
    async setFormsData() {
      store.commit('applicationRegistration/SET_FORMS_DATA')
      store.commit('applicationRegistration/SET_FORMS_VALUES_FROM_FAMILIES_FIELDS')
      store.commit('applicationRegistration/SET_FORMS_VALUES_FROM_USER_ATTRIBUTES')
      store.commit('applicationRegistration/SET_FORMS_VALUES_FROM_APPLICATION_FIELDS')
      store.commit('applicationRegistration/CHECK_CONDITIONS')
    },
    async setApplicationData() {
      try {
        const response = await store.dispatch('applications/fetchApplicationById', this.applicationId)

        // Check if application exists
        if (!response || !response.id) {
          this.$router.push({ name: 'error-404' })
          return
        }

        // Check sharing settings and user access
        if (!response.is_related_by_family_for_current_user && response.sharing_settings?.type === 'only_selected_accounts') {
          const loggedUserId = getUserData().id
          const isAvaliableForThisUser = response.sharing_settings.selectedAccounts
            .some(item => item === loggedUserId)
          if (!isAvaliableForThisUser) {
            this.$router.push({ name: 'error-404' })
            return
          }
        }

        // Set application data if all checks pass
        store.commit('applicationRegistration/SET_APPLICATION', response)
        if (response.payment_contract) {
          store.commit('applicationRegistration/SET_PAYMENT', response.payment_contract)
        }
      } catch (error) {
        console.error('Error fetching application data:', error)
        // Redirect to 404 on any error
        this.$router.push({ name: 'error-404' })
      }
    },
    // Called when a step submission is rejected with a 409 conflict because the
    // application/step state changed (e.g. an admin action) since this page was
    // loaded. Re-fetches the application and rebuilds all derived step state
    // (status, hidden/editable flags, form values) so the page truly reflects
    // the latest data - including the current editing_state_version - before
    // the user can submit again.
    async handleApplicationStale() {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'This application was updated elsewhere',
          text: 'We\'ve refreshed this page with the latest information. Please review and try again.',
          icon: 'AlertTriangleIcon',
          variant: 'warning',
        },
      })

      store.commit('SET_LOADING_STATUS', true)
      this.isDataFullPrepared = false
      try {
        await this.setApplicationData()
        // setApplicationData() only refreshes stepsValues/status/version on the
        // root state - step.status (used for editability/locking checks) and
        // preparedFields/values are derived and must be recalculated explicitly.
        store.commit('applicationRegistration/SET_STEP_STATUS')
        await this.prepareData()
      } finally {
        this.isDataFullPrepared = true
        store.commit('SET_LOADING_STATUS', false)
      }
    },
    async selectActiveTab() {
      this.isTabIndexReady = true
      const query = this.$router.currentRoute.query

      // Navigate directly to the PC step when arriving from the PC reminder link
      if (query.navigate_to_pc) {
        const pcStepIndex = this.filteredStepsList.findIndex(s => s.has_payment_contract)
        if (pcStepIndex !== -1) {
          const preRegOffset = this.preRegFormsList.length
          const familyOffset = this.getIsUseFamilyForm ? 1 : 0
          await this.$nextTick()
          this.tabIndex = preRegOffset + familyOffset + pcStepIndex
          return
        }
        // PC step not found — fall through to normal tab selection
      }

      const queryTabIndex = query.tab_index

      if (queryTabIndex) {
        await this.$nextTick()
        this.tabIndex = Number(queryTabIndex)
        return
      }

      const familyTabIndex = this.preRegFormsList.length
      const applicationStepsStartIndex = this.getIsUseFamilyForm ? (familyTabIndex + 1) : familyTabIndex

      // 1. Check pre-reg forms
      const statusPriority = ["Not Started", "Rejected", "In Review"]
      for (const status of statusPriority) {
        const index = this.preRegFormsList.findIndex(form => form.status === status)
        if (index !== -1) {
          this.tabIndex = index
          return
        }
      }

      // 2. If all pre-reg forms Approved
      if (this.isFamilyTabActive) {
        this.tabIndex = familyTabIndex
        return
      }

      // 3. Find the last Approved step in the Application Steps
      const lastApprovedIndex = this.filteredStepsList.map(s => s.status).lastIndexOf("Approved")

      await this.$nextTick()
      if (lastApprovedIndex === this.filteredStepsList.length - 1) {
        // all completed
        this.tabIndex = applicationStepsStartIndex + lastApprovedIndex
      } else if (lastApprovedIndex > -1) {
        // go to the last
        this.tabIndex = applicationStepsStartIndex + lastApprovedIndex + 1
      } else {
        this.tabIndex = applicationStepsStartIndex
      }
    },
    setSpecificIndex(index) {
      this.tabIndex = index
      this.setTabIndexQuery()
    },
    setTabIndexQuery() {
      this.$nextTick(() => {
        const query = Object.assign({}, this.$route.query)
        if (query.tab_index == this.tabIndex) return
        query.tab_index = this.tabIndex
        this.$router.push({ name: 'application-create', query })
      })
    },
    async enableSteps() {
      for (let i = 0; i < this.filteredStepsList.length; i++) {

        if (this.filteredStepsList[i].status == "Approved") {
          this.filteredStepsList[i].isDisabled = false
          if (i < this.filteredStepsList.length - 1) {
            this.filteredStepsList[i+1].isDisabled = false
          }
        } else if (this.filteredStepsList[i].requirement_option == "Required To Continue" 
         || this.filteredStepsList[i].requirement_option == "Requires Approval To Continue") {
          this.filteredStepsList[i].isDisabled = false
          break
        } else {
          this.filteredStepsList[i].isDisabled = false
        }

        if (this.isApplicationAccepted) {
          this.filteredStepsList[i].isDisabled = false
        }
      }
    },
    goNextTabIfAvailable() {
      this.enableSteps()
      this.$nextTick(() => {
        store.commit('applicationRegistration/INCREMENT_TAB_INDEX')
        this.setTabIndexQuery()
      })
    },
    stepIndex(val) {
      const index = val + 1
      return index < 10 ? `0${index}` : index
    },
    async prepareCrossProgramsConditions() {
      if (this.isExistCrossProgramsConditions) {
        store.commit('applicationRegistration/PREPARE_CROSS_PROGRAMS_CONDITIONAL_FIELDS_DATA')
        await store.dispatch('applicationRegistration/fetchUserFieldResults', {
            user_id: this.userId,
            fields_data: this.crossProgramConditionalFieldsData,
          })
        store.commit('applicationRegistration/PREPARE_CROSS_PROGRAMS_CONDITIONS')
      }
    },
    async prepareConditionsFromSteps() {
      store.commit('applicationRegistration/PREPARE_CONDITIONS_FROM_STEPS')
    },
    async checkIfAllFieldsInStepAreHidden() {
      store.commit('applicationRegistration/CHECK_IF_ALL_FIELDS_IN_STEP_ARE_HIDDEN')
    },
    toggleCollapseTabs(checkHeader = false) {
      if (!checkHeader || this.preRegFormsList.length === 0) {
        this.isCollapsed = !this.isCollapsed
      }
    },
    async disableEditingStepsIfpaymentContractIsExist() {
      store.commit('applicationRegistration/DISABLE_EDITING_STEPS_IF_PAYMENT_CONTRACT_IS_SELECTED')
    },
    async disableEditingStepsByEditingApplicationPermissions() {
      store.commit('applicationRegistration/SET_EDITABLE_SETTINGS_FOR_STEPS')
    },
    async checkHiddenStepsBeforeApplicationAcceptance() {
      store.commit('applicationRegistration/SET_HIDDEN_STATUS_FOR_STEPS_FROM_SETTINGS')
    }
  }
}
</script>

<style scoped>

</style>